import styled from '@emotion/styled';
import React, {
	ChangeEvent,
	InputHTMLAttributes,
	ReactNode,
	useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { VisuallyHiddenInput } from '../Input';
import { ITheme } from '../../themer';
import { Color } from '../../../features/shared/styles/Color';
import themed from '../../themer/themed';

interface IDefaultCheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
	icon: ReactNode;
	label: string;
	name: string;
	id: string;
}

export const CustomCheckbox = ({
	checked,
	disabled,
	icon,
	label,
	name,
	id,
	...props
}: IDefaultCheckboxProps) => {
	const [isChecked, setIsChecked] = useState(checked);
	const { register } = useFormContext();

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		setIsChecked(e.target.checked);
	};

	return (
		<div>
			<VisuallyHiddenInput
				id={id}
				type="checkbox"
				aria-labelledby={`${name}-checkbox`}
				checked={!!isChecked}
				disabled={disabled}
				{...props}
				{...register(name, {
					onChange: (e) => {
						handleChange(e);
					},
				})}
			/>

			<StyledCheckBoxLabel
				id={`${name}-checkbox`}
				htmlFor={id}
				isChecked={!!isChecked}
				disabled={!!disabled}
			>
				<StyledIconContainer>{icon}</StyledIconContainer>
				<StyledDefaultLabel>{label}</StyledDefaultLabel>
			</StyledCheckBoxLabel>
		</div>
	);
};

const StyledIconContainer = styled.div<{}, ITheme>`
	display: flex;
	margin-top: 1px;

	${VisuallyHiddenInput}:focus-visible + label & {
		outline: 3px solid ${Color.AccentDefault};
		outline-offset: 2px;
		border-radius: 50px;
	}
`;

const StyledDefaultLabel = styled.p`
	width: 100%;
	margin: 0;
`;

const StyledCheckBoxLabel = styled.label<
	{ isChecked: boolean; disabled: boolean },
	ITheme
>`
	cursor: ${({ disabled }) => (disabled ? 'auto' : 'pointer')};
	opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 6px 0;

	svg path {
		fill: ${themed((_, { isChecked }) =>
			isChecked ? Color.AlertDefault : 'transparent',
		)};
		stroke: ${themed((_, { isChecked }) =>
			isChecked ? Color.AlertDefault : '#A7A7A7',
		)};
	}
`;
